import React, { useEffect, useState } from 'react'
import TuneIcon from '@material-ui/icons/Tune'
import AssignmentIcon from '@material-ui/icons/Assignment'
import BarChartIcon from '@material-ui/icons/BarChart'
import TrackChangesIcon from '@material-ui/icons/TrackChanges'
import HistoryIcon from '@material-ui/icons/History'
import GetAppIcon from '@material-ui/icons/GetApp'
import ExploreIcon from '@material-ui/icons/Explore'
import SettingsIcon from '@material-ui/icons/Settings'
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf'
import TableChartIcon from '@material-ui/icons/TableChart'
import ReportProblemIcon from '@material-ui/icons/ReportProblem'
import LabelIcon from '@material-ui/icons/Label'
import CreateNewFolderIcon from '@material-ui/icons/CreateNewFolder'
import { Select, MenuItem } from '@material-ui/core'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@material-ui/core'

import { Container, Selector } from '../../components'
import { useParams } from 'react-router-dom'
import {
    Tab,
    Tabs,
    useTheme,
    IconButton,
    Tooltip,
    Theme,
} from '@material-ui/core'

import { useDeliverable, useLeaseAudit } from '../../hooks/documentAudit'
import { AuditPage, HistoryPage } from './Pages'

import { isAccountManager } from '../../helpers'
import { useUser } from '../../hooks'
import { toast } from 'react-toastify'

import { useCriteriaGroup } from '../../hooks/documentAudit/useCriteriaGroup'

import { useEntityClassifications } from '../../hooks/documentAudit/useEntityClassifications'
import { FilterValue, AuditFilterDrawer, FilterChips } from './Components'
import {
    ResidentTermDirectoryRow,
    MatchType,
    ResidentTermDirectoryStatus,
    MergeRequest,
    LeaseAuditStatus,
} from '../../models/DocumentAudit/types'

import { RentRollPage } from './Pages/RentRollPage'
import { AnalysisPage } from './Pages/AnalysisPage'
import { SettingsPage } from './Pages/SettingsPage'
import { useMerge } from '../../hooks/documentAudit/useMerge'
import { MissingErrors } from './Pages/MissingErrors'
import { getLeaseAuditStatus } from '../../models/DocumentAudit/services'
import { LeasePage } from './Pages/LeasePage'
import { EntityClassConfig } from './Pages/EntityClassConfig'
// Add the DataSourceLegend component
const DataSourceLegend = ({ theme }: { theme: Theme }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '16px',
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        marginRight: '8px',
                        backgroundColor: 'rgba(255, 253, 231, 0.5)',
                        border: `1px solid ${theme.palette.warning.light}`,
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                    }}
                >
                    <PictureAsPdfIcon
                        style={{
                            fontSize: 14,
                            marginRight: '4px',
                            color: theme.palette.warning.dark,
                        }}
                    />
                    PDF Document Only
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        marginRight: '8px',
                        backgroundColor: 'rgba(255, 248, 225, 0.5)',
                        border: `1px solid ${theme.palette.warning.main}`,
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                    }}
                >
                    <TableChartIcon
                        style={{
                            fontSize: 14,
                            marginRight: '4px',
                            color: theme.palette.warning.dark,
                        }}
                    />
                    Excel Data Only
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                    style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        marginRight: '8px',
                        backgroundColor: 'rgba(255, 235, 235, 0.5)',
                        border: `1px solid ${theme.palette.error.main}`,
                    }}
                />
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                    }}
                >
                    <ReportProblemIcon
                        style={{
                            fontSize: 14,
                            marginRight: '4px',
                            color: theme.palette.error.main,
                        }}
                    />
                    Mismatched Values
                </div>
            </div>
        </div>
    )
}

const LEASE_AUDIT_STATUSES: LeaseAuditStatus[] = [
    'REQUESTED',
    'SETUP',
    'WAITING_FOR_APPROVAL',
    'RUNNING_PIPELINE',
    'COMPLETED',
    'APPROVED',
    'REJECTED',
]

export const LeaseAuditRoot = () => {
    const { id } = useParams<{ id?: string }>()
    const { workspaceUser } = useUser()
    const theme = useTheme()

    const getDemensions = () => {
        const topHeaderH = 50 // Reduced from 84 to 50 since we're using single row
        const footerH = 50
        const containerH = window.innerHeight - 120
        const containerW = window.innerWidth - 120
        const bodyH = containerH - topHeaderH - footerH // Subtract both headers and footer

        console.log(containerW)

        return {
            topHeaderH: topHeaderH,
            footerH: footerH,
            containerH: containerH,
            bodyH: bodyH,
            containerW: containerW,
        }
    }

    const [dimensions, setDimensions] = useState(getDemensions())

    useEffect(() => {
        const handleResize = () => {
            setDimensions(getDemensions())
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)

    const [tab, setTab] = useState<number>(0)

    const leaseAuditController = useLeaseAudit()
    const superUser = isAccountManager(workspaceUser)
    const {
        criteriaGroups,
        createCriteriaGroup,
        updateCriteriaGroup,
    } = useCriteriaGroup()

    const {
        entityClassifications,
        isLoading: isLoadingEntityClassifications,
    } = useEntityClassifications()

    const {
        deliverable,
        isLoadingDeliverable,
        updateDeliverableValueMessageCount,
        bulkAcceptDeliverableValues,
    } = useDeliverable(Number(id))

    const { mergeResidentTermDirectories, isMerging } = useMerge(Number(id))

    // Create a wrapper function with the correct type signature
    const handleBulkAccept = async (
        deliverableValueIds: number[],
        notes: string,
    ): Promise<void> => {
        // Use the deliverable from the state instead of leaseAuditController.leaseAudit
        if (!deliverable?.id) {
            toast.error('No active deliverable found')
            return
        }

        await bulkAcceptDeliverableValues({
            deliverableId: deliverable.id,
            deliverableValueIds,
            notes,
        })
    }

    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
    const drawerWidth = 340

    const [selectedFilters, setSelectedFilters] = useState<
        Record<number, FilterValue>
    >({})

    const [hasMessagesFilter, setHasMessagesFilter] = useState(false)

    // Initialize with an empty array to show all statuses by default (no boxes checked)
    const [statusFilter, setStatusFilter] = useState<
        ResidentTermDirectoryStatus[]
    >([])

    const [timePeriodFilter, setTimePeriodFilter] = useState<
        'current' | 'future' | 'past' | 'all'
    >('all')

    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)

    useEffect(() => {
        if (id) {
            const numId = Number(id)
            leaseAuditController.fetchLeaseAudit(numId)
            leaseAuditController.getRentRoll(numId)
            leaseAuditController.fetchAnalysisData(numId)
            leaseAuditController.fetchDocumentBatches(numId)
            leaseAuditController.getEntityClasses()
        }
    }, [id])

    const criteriaGroup = criteriaGroups.find(
        (group) => group.id === leaseAuditController.leaseAudit?.criteria_group,
    )

    const filterData = (data: ResidentTermDirectoryRow[]) => {
        // Apply time period filter
        let filteredData = data
        console.log('data', data)

        // Only apply time filter when we have data
        if (data && data.length > 0) {
            const today = new Date()
            today.setHours(0, 0, 0, 0) // Set to beginning of day for accurate comparison

            filteredData = data.filter((item) => {
                const leaseStart = new Date(item.lease_start)
                const leaseEnd = new Date(item.lease_end)

                switch (timePeriodFilter) {
                    case 'all':
                        // All: show all leases
                        return true
                    case 'future':
                        // Future: lease hasn't started yet
                        return leaseStart > today
                    case 'past':
                        // Past: lease has already ended
                        return leaseEnd < today
                    case 'current':
                    default:
                        // Current: lease has started and not ended yet
                        return leaseStart <= today && leaseEnd >= today
                }
            })
        }

        // Apply message filter if enabled
        if (hasMessagesFilter) {
            filteredData = filteredData.filter((item) =>
                item.values.some((value) => value.message_count > 0),
            )
        }

        // Apply status filter only if any statuses are selected
        // If no statuses are selected, show all statuses
        if (statusFilter.length > 0) {
            filteredData = filteredData.filter((item) =>
                statusFilter.includes(item.status),
            )
        }

        // Apply existing filters
        if (Object.keys(selectedFilters).length === 0) return filteredData

        return filteredData.filter((item) => {
            for (const [classificationId, filter] of Object.entries(
                selectedFilters,
            )) {
                if (!filter.enabled) continue

                const entity = item.values?.find(
                    (e) =>
                        e.entity_classification.id === Number(classificationId),
                )

                if (!entity) return false

                const value = entity.display_value
                const type = entity.entity_classification.data_type

                // If no value found for this classification, exclude the item
                if (value === undefined) return false

                switch (type) {
                    case 'NUMBER':
                    case 'CURRENCY':
                        if (!handleNumericFilter(value, filter)) return false
                        break
                    case 'TEXT':
                        // For text, treat min as search term
                        if (
                            filter.min &&
                            !value
                                .toString()
                                .toLowerCase()
                                .includes(filter.min.toString().toLowerCase())
                        ) {
                            return false
                        }
                        break
                    case 'BOOLEAN':
                        // For boolean, treat min as the expected value (true/false)
                        if (filter.min != null && value !== filter.min)
                            return false
                        break
                    case 'DATE':
                        if (!handleDateFilter(value, filter)) return false
                        break
                }
            }
            return true
        })
    }

    const filteredDirectories = filterData(
        deliverable?.resident_term_directories ?? [],
    )

    // Update the filter change handler to reset pagination
    const handleFilterChange = (newFilters: Record<number, FilterValue>) => {
        setSelectedFilters(newFilters)
        setPage(0)
    }

    // Add handler for time period filter change
    const handleTimePeriodFilterChange = (
        period: 'current' | 'future' | 'past' | 'all',
    ) => {
        setTimePeriodFilter(period)
        setPage(0) // Reset pagination when filter changes
    }

    const handleRowsPerPageChange = (rowsPerPage: number) => {
        setRowsPerPage(rowsPerPage)
        setPage(0)
    }

    const handleCreateGroup = (name: string) => {
        // Create initial criteria for all entity classifications
        const initialCriteria = entityClassifications.map((entityClass) => ({
            entity_class_id: entityClass.id,
            match_type: 'NOT_REQUIRED' as MatchType,
            display: true, // Initialize display to true
        }))

        createCriteriaGroup({
            name,
            criteria: initialCriteria,
        })
    }

    const handleSelectGroup = async (groupId: number) => {
        if (!leaseAuditController.leaseAudit?.id) return

        // Call the setCriteriaGroup function from useLeaseAudit
        await leaseAuditController.setCriteriaGroup(
            groupId,
            leaseAuditController.leaseAudit.id,
        )
    }

    // Update the clear all function to reset time period filter to 'current' instead of 'all'
    const handleClearAllFilters = () => {
        // Clear entity class filters
        const clearedFilters: Record<number, FilterValue> = {}
        entityClassifications.forEach((classification) => {
            clearedFilters[classification.id] = {
                id: classification.id,
                enabled: false,
                min: null,
                max: null,
            }
        })

        // Reset to empty status filter array (no boxes checked)
        setStatusFilter([])

        // Clear message filter
        setHasMessagesFilter(false)

        // Reset time period filter to 'current' instead of 'all'
        setTimePeriodFilter('current')

        // Apply all changes
        setSelectedFilters(clearedFilters)
        setPage(0)
    }

    // Handle status filter change
    const handleStatusFilterChange = (
        statuses: ResidentTermDirectoryStatus[],
    ) => {
        setStatusFilter(statuses)
        setPage(0) // Reset pagination when filter changes
    }

    // Determine if any filters are active for UI purposes
    const hasActiveFilters = () => {
        // Check if any entity class filters are active
        const hasActiveEntityFilters = Object.values(selectedFilters).some(
            (filter) => filter.enabled,
        )

        // Check if status filter has any selected statuses
        const hasActiveStatusFilter = statusFilter.length > 0

        // Check if messages filter is active
        const hasActiveMessagesFilter = hasMessagesFilter

        const hasActiveTimePeriodFilter = timePeriodFilter !== 'all'

        return (
            hasActiveEntityFilters ||
            hasActiveStatusFilter ||
            hasActiveMessagesFilter ||
            hasActiveTimePeriodFilter
        )
    }

    const handleMergeResidentTermDirectories = async (params: MergeRequest) => {
        await mergeResidentTermDirectories(params)
    }

    const handleStatusChange = async (newStatus: LeaseAuditStatus) => {
        if (!leaseAuditController.leaseAudit?.id) return
        const result = await leaseAuditController.updateLeaseAudit(
            leaseAuditController.leaseAudit.id,
            { status: newStatus },
        )
        if (result) {
            toast.success('Status updated successfully')
        }
    }

    const handleCreateDeliverable = () => {
        if (!leaseAuditController.leaseAudit?.id) return
        setConfirmDialogOpen(false)
        leaseAuditController.createDeliverable(
            leaseAuditController.leaseAudit.id,
        )
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                height: dimensions.containerH,
                width: dimensions.containerW,
            }}
        >
            {/* Top Header */}
            <Container
                style={{
                    flexDirection: 'row',
                    height: dimensions.topHeaderH,
                    borderBottom: '1px solid #e0e0e0',
                    backgroundColor: theme.palette.background.paper,
                    padding: '0 24px',
                    alignItems: 'center',
                    gap: '24px',
                }}
            >
                {/* Title */}
                <h2 style={{ margin: 0, whiteSpace: 'nowrap' }}>
                    {leaseAuditController.leaseAudit?.name}
                </h2>

                {/* Navigation tabs */}
                <Tabs
                    value={tab}
                    onChange={(_, newValue) => {
                        console.log('newValue', newValue)
                        setTab(newValue)
                    }}
                    indicatorColor="primary"
                    textColor="primary"
                    style={{ minHeight: '34px', flex: 1 }}
                >
                    <Tooltip title="Audit">
                        <Tab
                            icon={<AssignmentIcon />}
                            aria-label="Audit"
                            style={{ minWidth: 50 }}
                            title="Audit"
                            value={AUDIT_TAB}
                        />
                    </Tooltip>
                    <Tooltip title="Analysis">
                        <Tab
                            icon={<BarChartIcon />}
                            aria-label="Analysis"
                            style={{ minWidth: 50 }}
                            title="Analysis"
                            value={ANALYSIS_TAB}
                        />
                    </Tooltip>
                    <Tooltip title="Settings">
                        <Tab
                            icon={<SettingsIcon />}
                            aria-label="Settings"
                            style={{ minWidth: 50 }}
                            title="Settings"
                            value={SETTINGS_TAB}
                        />
                    </Tooltip>
                    {superUser && (
                        <Tooltip title="Rent Roll">
                            <Tab
                                icon={<TableChartIcon />}
                                aria-label="Rent Roll"
                                style={{ minWidth: 50 }}
                                title="Rent Roll"
                                value={RENT_ROLL_TAB}
                            />
                        </Tooltip>
                    )}

                    {superUser && (
                        <Tooltip title="Leases">
                            <Tab
                                icon={<PictureAsPdfIcon />}
                                aria-label="Integrated Leases"
                                style={{ minWidth: 50 }}
                                title="Integrated Leases"
                                value={LEASES_TAB}
                            />
                        </Tooltip>
                    )}

                    {superUser && (
                        <Tooltip title="History">
                            <Tab
                                icon={<HistoryIcon />}
                                aria-label="History"
                                style={{ minWidth: 50 }}
                                title="History"
                                value={HISTORY_TAB}
                            />
                        </Tooltip>
                    )}

                    {superUser && (
                        <Tooltip title="Missing Errors">
                            <Tab
                                icon={<TrackChangesIcon />}
                                aria-label="MissingErrors"
                                style={{ minWidth: 50 }}
                                title="MissingErrors"
                                value={MISSING_ERRORS_TAB}
                            />
                        </Tooltip>
                    )}
                    {superUser && (
                        <Tooltip title="Prompt">
                            <Tab
                                icon={<LabelIcon />}
                                aria-label="Prompt"
                                style={{ minWidth: 50 }}
                                title="Prompt Config"
                                value={ENTITY_CLASS_CONFIG_TAB}
                            />
                        </Tooltip>
                    )}
                </Tabs>

                {/* Show the legend only when on the Audit tab */}
                {/* {tab === AUDIT_TAB && <DataSourceLegend theme={theme} />} */}

                {superUser && (
                    <Tooltip title="Create New Deliverable">
                        <IconButton onClick={() => setConfirmDialogOpen(true)}>
                            <CreateNewFolderIcon />
                        </IconButton>
                    </Tooltip>
                )}

                {/* Status Selector */}
                {superUser && (
                    <Select
                        value={leaseAuditController.leaseAudit?.status || ''}
                        onChange={(e) =>
                            handleStatusChange(
                                e.target.value as LeaseAuditStatus,
                            )
                        }
                        style={{ minWidth: 150 }}
                    >
                        {LEASE_AUDIT_STATUSES.map((status) => (
                            <MenuItem key={status} value={status}>
                                {getLeaseAuditStatus(status)}
                            </MenuItem>
                        ))}
                    </Select>
                )}

                {/* Generate Report Button */}
                <Tooltip title="Generate Report">
                    <IconButton
                        onClick={() => {
                            if (leaseAuditController.leaseAudit?.id) {
                                leaseAuditController.generateReport(
                                    leaseAuditController.leaseAudit.id,
                                )
                            }
                        }}
                    >
                        <GetAppIcon />
                    </IconButton>
                </Tooltip>

                {/* filter Button */}
                <Tooltip title="Filter">
                    <IconButton onClick={() => setFilterDrawerOpen(true)}>
                        <TuneIcon />
                    </IconButton>
                </Tooltip>
            </Container>

            {/* Add the FilterChips component here */}
            <div
                style={{
                    paddingLeft: theme.spacing(2),
                    paddingRight: theme.spacing(2),
                }}
            >
                <FilterChips
                    theme={theme}
                    selectedFilters={selectedFilters}
                    entityClassifications={entityClassifications}
                    onFilterChange={handleFilterChange}
                    hasMessagesFilter={hasMessagesFilter}
                    onHasMessagesFilterChange={(value) => {
                        setHasMessagesFilter(value)
                        setPage(0) // Reset pagination when filter changes
                    }}
                    statusFilter={statusFilter}
                    onStatusFilterChange={handleStatusFilterChange}
                    timePeriodFilter={timePeriodFilter}
                    onTimePeriodFilterChange={handleTimePeriodFilterChange}
                />
            </div>

            {/* Main content with transition */}
            <div
                style={{
                    transition: theme.transitions.create(['margin'], {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.leavingScreen,
                    }),
                    marginRight: filterDrawerOpen ? drawerWidth : 0,
                    height: dimensions.bodyH,
                }}
            >
                {/* Wrap existing tab content */}
                {tab === AUDIT_TAB && (
                    <AuditPage
                        residentTermDirectories={filteredDirectories}
                        theme={theme}
                        criteriaGroup={criteriaGroup}
                        bodyH={dimensions.bodyH}
                        footerH={dimensions.footerH}
                        isLoading={isLoadingDeliverable}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        setRowsPerPage={handleRowsPerPageChange}
                        deliverableId={deliverable?.id ?? -1}
                        leaseAuditId={Number(id) || -1}
                        updateDeliverableValueMessageCount={
                            updateDeliverableValueMessageCount
                        }
                        entityClassifications={entityClassifications}
                        bulkAcceptDeliverableValues={handleBulkAccept}
                        mergeResidentTermDirectories={
                            handleMergeResidentTermDirectories
                        }
                        isMerging={isMerging}
                    />
                )}
                {tab === ANALYSIS_TAB && (
                    <AnalysisPage
                        theme={theme}
                        leaseAuditController={leaseAuditController}
                        criteriaGroup={criteriaGroup}
                    />
                )}
                {tab === SETTINGS_TAB && (
                    <SettingsPage
                        theme={theme}
                        height={dimensions.bodyH}
                        criteriaGroups={criteriaGroups}
                        selectedGroupId={
                            leaseAuditController.leaseAudit?.criteria_group
                        }
                        onSelectGroup={handleSelectGroup}
                        onCreateGroup={handleCreateGroup}
                        onUpdateMatchType={(groupId, criteriaId, matchType) => {
                            updateCriteriaGroup({
                                id: groupId,
                                criteria: [
                                    {
                                        entity_class_id: criteriaId,
                                        match_type: matchType,
                                    },
                                ],
                            })
                        }}
                        onUpdateDisplayField={(
                            groupId,
                            criteriaId,
                            display,
                        ) => {
                            updateCriteriaGroup({
                                id: groupId,
                                criteria: [
                                    {
                                        entity_class_id: criteriaId,
                                        display,
                                    },
                                ],
                            })
                        }}
                        onUpdateDescription={(
                            groupId,
                            criteriaId,
                            prompt_description,
                        ) => {
                            updateCriteriaGroup({
                                id: groupId,
                                criteria: [
                                    {
                                        entity_class_id: criteriaId,
                                        prompt_description,
                                    },
                                ],
                            })
                        }}
                        onUpdateOrder={(groupId, reorderedCriteria) => {
                            updateCriteriaGroup({
                                id: groupId,
                                criteria: reorderedCriteria,
                            })
                        }}
                    />
                )}
                {tab === RENT_ROLL_TAB && superUser && (
                    <RentRollPage
                        theme={theme}
                        bodyH={dimensions.bodyH}
                        footerH={dimensions.footerH}
                        leaseAuditController={leaseAuditController}
                        isActive={tab === RENT_ROLL_TAB}
                    />
                )}
                {tab === LEASES_TAB && superUser && (
                    <LeasePage
                        theme={theme}
                        height={dimensions.bodyH}
                        leaseAuditController={leaseAuditController}
                    />
                )}
                {tab === HISTORY_TAB && superUser && (
                    <HistoryPage theme={theme} isActive={tab === HISTORY_TAB} />
                )}

                {tab === MISSING_ERRORS_TAB && superUser && (
                    <MissingErrors
                        theme={theme}
                        isActive={tab === MISSING_ERRORS_TAB}
                        leaseAuditId={Number(id) || -1}
                    />
                )}
                {tab === ENTITY_CLASS_CONFIG_TAB && superUser && (
                    <EntityClassConfig
                        theme={theme}
                        isActive={tab === ENTITY_CLASS_CONFIG_TAB}
                        entityClasses={leaseAuditController.entityClasses}
                        updateEntityClassDescription={
                            leaseAuditController.updateEntityClassDescription
                        }
                    />
                )}
            </div>

            <AuditFilterDrawer
                open={filterDrawerOpen}
                onClose={() => setFilterDrawerOpen(false)}
                theme={theme}
                drawerWidth={drawerWidth}
                selectedFilters={selectedFilters}
                onFilterChange={handleFilterChange}
                entityClassifications={entityClassifications}
                criteriaGroup={criteriaGroup}
                isLoading={isLoadingEntityClassifications}
                hasMessagesFilter={hasMessagesFilter}
                onHasMessagesFilterChange={(value) => {
                    setHasMessagesFilter(value)
                    setPage(0) // Reset pagination when filter changes
                }}
                statusFilter={statusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                onClearAll={handleClearAllFilters}
                hasActiveFilters={hasActiveFilters()}
                timePeriodFilter={timePeriodFilter}
                onTimePeriodFilterChange={handleTimePeriodFilterChange}
            />
            <Dialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
            >
                <DialogTitle>Confirm Create Deliverable</DialogTitle>
                <DialogContent>
                    Are you sure you want to create a new deliverable?
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setConfirmDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCreateDeliverable}
                        color="primary"
                        variant="contained"
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}

const handleNumericFilter = (value: string | number, filter: FilterValue) => {
    if (typeof value === 'string') {
        value = Number(value)
    }

    const filterMin = filter.min ? Number(filter.min) : null
    const filterMax = filter.max ? Number(filter.max) : null

    if (filterMin != null && value < filterMin) return false
    if (filterMax != null && value > filterMax) return false
    return true
}

const handleDateFilter = (value: string | number, filter: FilterValue) => {
    let dateValue: Date | null = null

    if (typeof value === 'string') {
        dateValue = new Date(value)
    } else if (typeof value === 'number') {
        dateValue = new Date(value)
    }

    if (dateValue == null) return false

    const filterMin = filter.min ? new Date(filter.min) : null
    const filterMax = filter.max ? new Date(filter.max) : null

    if (filterMin != null && dateValue < filterMin) return false
    if (filterMax != null && dateValue > filterMax) return false
    return true
}

// user facing tabs
const AUDIT_TAB = 0
const ANALYSIS_TAB = 1
const SETTINGS_TAB = 2

// admin tabs
const RENT_ROLL_TAB = 3
const LEASES_TAB = 4

// soon to be deprecated tabs
const HISTORY_TAB = 5
const MISSING_ERRORS_TAB = 6
const ENTITY_CLASS_CONFIG_TAB = 7
